/**
 * @module leadGeneration.mjs
 * @description This is a store for the "Eigenrecherche" feature. It is used to create a list of businessPartner leads based on a googlePlaces search
 */

import { defineStore } from "pinia";

import { useUserStore } from "./user";
import { useBusinessPartnersCountStore } from '@/stores/businessPartnersCount';


import { fetchFromBackend } from "./helpers/backend.mjs";

export const useLeadGenerationStore = defineStore("leadGeneration", {
  state: () => {
    return {
      leadLists: [], // lists of businessPartners, typically based on a google places search
      eigenrechercheCount: 0,
      isLoadingEigenrechercheCount: false,
      matchLeadsIsAvailable: false, // null = noch nicht geprüft, true = erreichbar, false = nicht erreichbar (z.B. aufgrund von Backend-Problemen)
    }
  },
  getters: {
    leadList: (state) => {
      const userStore = useUserStore();
      const leadListExists = state.leadLists.find((list) => list.signature === userStore.currentSelection.leadGeneration.signature);
      return (leadListExists) ? leadListExists : { summary: '', data: [], size: 0 };
    },

  },
  actions: {
    /** Get Count of all self searched results (Eigenrecherche) for the current campaign and salesAgent
     * @async
    */
    async fetchEigenrechercheCount() {
      const userStore = useUserStore();
      console.log('fetchEigenrechercheCount: start fetching count for Eigenrecherche');
      console.log('fetchEigenrechercheCount: userId:', userStore.id, 'currentSelection.salesAgent.id:', userStore.currentSelection?.salesAgent?.id);
      const businessPartnersCountStore = useBusinessPartnersCountStore();
      const filter = businessPartnersCountStore.namedFilter('eigenrechercheUnbeendetCount');
      const targetSignature = filter?.signature;
      console.log('fetchEigenrechercheCount: filter used:', JSON.stringify(filter));
      this.isLoadingEigenrechercheCount = true;
      try {
        await businessPartnersCountStore.getByFilter(filter);
        // Log nach await, um sicherzustellen, dass die Daten geladen sind
        console.log('fetchEigenrechercheCount: businessPartnerLists:', businessPartnersCountStore.businessPartnerLists);
        // Exakt den Eintrag zur aktuellen Signatur ermitteln (kein includes, da sonst Team-/SM-Mischung möglich ist)
        const lists = businessPartnersCountStore.businessPartnerLists.find(l =>
          (l.signature && l.signature === targetSignature) ||
          (l.filter && l.filter.signature && l.filter.signature === targetSignature)
        );
        console.log('fetchEigenrechercheCount: lists for signature', targetSignature, ':', lists);
        let count = 0;
        if (lists && typeof lists.count === 'number') {
          count = lists.count;
          console.log('fetchEigenrechercheCount: count from lists.count:', count);
        } else {
          console.log('fetchEigenrechercheCount: no valid count found, defaulting to 0');
        }
        this.eigenrechercheCount = count;
        console.log('fetchEigenrechercheCount: eigenrechercheCount set to:', this.eigenrechercheCount);
      } finally {
        this.isLoadingEigenrechercheCount = false;
      }
    },
    /** Verarbeitet eine Google-Places-Suche der Eigenrecherche
     * @async
     * @param {string} query - Der Suchstring, z.B. 'Maler Krefeld'
     * @returns {Promise<void>} Keine Rückgabe, die Funktion aktualisiert den state mit der Liste der gefundenen Leads und deren Matching-Status
    */
    async processQuery(query) {

      const userStore = useUserStore();
      if (!userStore.currentSelection.leadGeneration) userStore.currentSelection.leadGeneration = {};
      if (!userStore.currentSelection.leadGeneration.search) userStore.currentSelection.leadGeneration.search = {};
      userStore.currentSelection.leadGeneration.search.query = query;

      // Schritt 1: Den Suchstring über die Google Places API suchen, um die aktuelle leadList mit potenziellen Leads zu füllen
      await this.queryGooglePlaces(query);

      function getMatchedTokenRatio(tokenGroup) {
        const tokens = tokenGroup?.tokens;
        if (!Array.isArray(tokens) || tokens.length === 0) return 0;
        const matched = tokens.filter((t) => t && t.match === true).length;
        return matched / tokens.length;
      }

      function buildQualityAssessment(lead) {
        if (!lead?.match) {
          return {
            score: 0,
            bucket: 'none',
            reasons: [
              'Kein Bestandstreffer gefunden',
              'Direkte Uebernahme als neuer Lead moeglich'
            ]
          };
        }

        const confidence = Number(lead.match?.confidence || 0);
        const distance = Number(lead.match?.distance || 0);
        const nameRatio = getMatchedTokenRatio(lead.matching?.name);
        const addressRatio = getMatchedTokenRatio(lead.matching?.address);
        const phoneRatio = getMatchedTokenRatio(lead.matching?.phoneNumbers);
        const urlRatio = getMatchedTokenRatio(lead.matching?.urls);

        const confidencePoints = Math.min(100, Math.round(confidence * 100));
        const proximityBonus = Math.max(0, 20 - Math.round(distance * 1.5));
        const tokenPoints = Math.round((nameRatio * 30) + (addressRatio * 25) + (phoneRatio * 15) + (urlRatio * 10));
        const rawScore = Math.round((confidencePoints * 0.55) + (tokenPoints * 0.35) + (proximityBonus * 0.10));
        const score = Math.max(0, Math.min(100, rawScore));

        const reasons = [];
        if (distance > 5) reasons.push('Niedrige Qualitaet wegen hoher Distanz');
        else if (distance <= 1) reasons.push('Kurze Distanz zwischen Adressen');

        if (confidence >= 0.75) reasons.push('Sehr hohe Matching-Confidence');
        else if (confidence >= 0.5) reasons.push('Mittlere Matching-Confidence');
        else reasons.push('Niedrige Matching-Confidence');

        if (nameRatio >= 0.7) reasons.push('Name passt stark');
        else if (nameRatio <= 0.2) reasons.push('Name passt nur schwach');

        if (addressRatio <= 0.2) reasons.push('Adresse passt nur schwach');
        if (lead.meta?.googlePlaces?.phoneNumbers?.length) {
          if (phoneRatio === 0) reasons.push('Telefon passt ueberhaupt nicht');
          else if (phoneRatio <= 0.2) reasons.push('Telefon passt nur schwach');
        }

        if (lead.match?.status?.contracts && lead.match.status.contracts !== '--- -- -- --') {
          reasons.push('Bestandskunde mit aktiven Vertraegen');
        }

        let bucket = 'medium';
        if (score >= 75) bucket = 'high';
        else if (score < 45) bucket = 'low';

        return { score, bucket, reasons };
      }

      function normalizeGpNo(gpNo) {
        const value = String(gpNo || '').trim();
        if (!value) return '';
        return value.startsWith('SMA-') ? value.replace('SMA-', '') : value;
      }

      // Schritt 2: Jeden zurückgelieferten Google Places Lead prüfen
      for (let idx = 0; idx < this.leadList.data.length; idx++) {
        const lead = this.leadList.data[idx];
        console.log(`processQuery: handling lead #${idx} id=${lead?.meta?.googlePlaces?.id || lead?.id} name="${lead?.name || ''}" startProcessed=${lead.processed}`);
        Object.assign(lead, {
          matching: { score: 0, status: null },
          convertToBusinessPartner: 'allow', //default Wert
          // ensure locked is always defined on the lead (0/1)
          locked: (lead.locked === 1 || lead.locked === true) ? 1 : 0,
          selection: {
            google: false,
            businessPartner: false,
          }
        })

        // War: Zunächst über die google id prüfen ob wir ihn im SMA schon kennen, um unnötige Matching Anfragen zu vermeiden
        // Ist: Es wird immer ein Matching durchgeführt, da die Google Id veraltet sein kann und in der Zwischenzeit in SUGAR Account existieren könnte
        lead.match = null;
        // Der Lead muss gegen den BPI gematched werden, um zu schauen ob es ähnliche Einträge gibt
        let matchingResult = null;
        try {
          matchingResult = await this.matchLeadAgainstBusinessPartnerIndex(lead);
        } catch (err) {
          console.error('Exception while matching lead against business partner index:', err);
          lead.error = true;
          lead.errorMessage = err?.message || String(err || 'Unbekannter Fehler');
          lead.processed = true;
          lead.convertToBusinessPartner = "block";
          console.log(`processQuery: lead #${idx} marked error due to exception message=${lead.errorMessage}`);
          continue;
        }

        if (matchingResult?.code === 'ERROR') {
          console.error('Matching failed for lead', lead, 'with error:', matchingResult.message);
          lead.error = true;
          lead.errorMessage = matchingResult?.message || 'Matching Fehler vom Backend';
          lead.processed = true;
          lead.convertToBusinessPartner = "block";
          console.log(`processQuery: lead #${idx} marked error due to matchingResult.code===ERROR message=${lead.errorMessage}`);
          continue;
        } else if (matchingResult?.candidates && matchingResult.candidates.length > 0) {
          // Alle Kandidaten gegen den SMA prüfen ob diese Bestandskunden oder einem SM zugewiesen sind
          // console.log("candidates:", matchingResult.candidates)
          let chosenStatus = null;
          const getStatusOfAllCandidates = []
          for (const candidate of matchingResult.candidates) {
            const normalizedGpNo = normalizeGpNo(candidate?.meta?.gpNo);
            if (!normalizedGpNo) continue;
            getStatusOfAllCandidates.push(this.getBusinessPartnerStatus({ gpNo: normalizedGpNo, confidence: candidate.confidence }));
          }
          const candidatesWithStatus = await Promise.all(getStatusOfAllCandidates);
          chosenStatus = candidatesWithStatus.find((candidate) => candidate.confidence > 0.6 && candidate.status?.contracts !== '--- -- -- --');
          if (!chosenStatus) chosenStatus = candidatesWithStatus.find((candidate) => candidate.confidence > 0.3 && candidate.status?.idCurrentSalesPitch);
          if (!chosenStatus) chosenStatus = candidatesWithStatus.find((candidate) => candidate.confidence > 0.3 && candidate.status?.salesAgent);
          if (!chosenStatus) chosenStatus = { gpNo: normalizeGpNo(matchingResult.candidates[0]?.meta?.gpNo) };
          const chosenCandidate = matchingResult.candidates.find(
            (candidate) => normalizeGpNo(candidate?.meta?.gpNo) === normalizeGpNo(chosenStatus?.gpNo)
          ) || matchingResult.candidates[0];

          if (!chosenCandidate) {
            lead.error = true;
            lead.errorMessage = 'Kein passender Matching-Kandidat gefunden';
            lead.processed = true;
            lead.convertToBusinessPartner = 'block';
            console.log(`processQuery: lead #${idx} marked error because chosenCandidate is missing`);
            continue;
          }

          lead.match = {
            id: chosenCandidate.id,
            confidence: chosenCandidate.confidence,
            distance: chosenCandidate.distance,
            status: chosenStatus.status,
            name: chosenCandidate.name,
            meta: {
              source: 'matching',
              gpNo: normalizeGpNo(chosenCandidate.meta.gpNo),
              normalization: {
                idGoogle: lead.meta.googlePlaces.id,
                idBusinessPartner: chosenCandidate.id,
                address: chosenCandidate.meta.normalization?.address || chosenCandidate.address || '',
                phoneNumbers: (chosenCandidate.meta.normalization?.phoneNumbers) ? chosenCandidate.meta.normalization.phoneNumbers : null,
                urls: (chosenCandidate.meta.normalization?.urls) ? chosenCandidate.meta.normalization.urls : null
              }
            }
          };
          // evaluate how close the match is to the lead
          if (lead.match) {
            // set locked flag from chosen candidate status (always define as 0/1)
            if (chosenStatus) {
              lead.locked = (chosenStatus.locked === 1 || chosenStatus.locked === true) ? 1 : 0;
              if (lead.locked === 1) {
                lead.convertToBusinessPartner = 'block';
                lead.convertToBusinessPartnerColor = 'unavailable';
                lead.matching.reasons = (lead.matching.reasons || []);
                lead.matching.reasons.push('Zentrale Sperre');
                lead.matching.locked = true;
              }
            }
            lead.matching.status = lead.match.status?.contracts;
            console.log(lead.name + " -- 1 --PEPSI matching status: lead.matching", lead.match.status?.contracts);
            if (lead.match.status?.contracts && lead.match.status?.contracts !== '--- -- -- --') {
              // for now block the leadConversion if the status shows the businessPartner has contracts
              lead.convertToBusinessPartner = "block"
              console.log(lead.name + " -- 2 -- PEPSI matching status: lead.matching", lead.match.status?.contracts);
              if (lead.match.confidence >= 0.5) {
                console.log(lead.name + " -- 3 -- PEPSI matching status: lead.matching", lead.match.status?.contracts);
                lead.convertToBusinessPartnerColor = "unavailable"
                lead.matching.score = 2 + lead.match.confidence;
              } else {
                console.log(lead.name + " -- 4 -- PEPSI matching status: lead.matching", lead.match.status?.contracts);
                lead.convertToBusinessPartnerColor = "check"
                lead.matching.score = 1 + lead.match.confidence;
              }
            } else if (lead.match.confidence >= 0.5) {
              //	Neukunden(matched auf GP mit hohe Wahrscheinlichkeit):
              console.log(lead.name + " -- 5 -- PEPSI matching status: lead.matching", lead.match.status?.contracts);
              if (lead.match.status?.idCurrentSalesPitch) {
                // o	Rot: GP ist bei anderem SM oder auch in eigener Bearbeitung(aktive WVL oder Termin) => es wird die Übernahme blockiert, es wird angezeigt „in Bearbeitung: [Status / TBD Name SM]“
                // lead.match.status.contracts = `- i.B. ${lead.match.status.salesAgent.firstName} ${lead.match.status.salesAgent.lastName} -`
                console.log(lead.name + " -- 6 -- PEPSI matching status: lead.matching", lead.match.status?.contracts);
                lead.convertToBusinessPartnerColor = "unavailable"
                lead.matching.score = 2 + lead.match.confidence;
              } else {
                // o	HellGrün: GP ist nicht Bearbeitung => SM hat nur die Möglichkeit den GP zur Übernahme auszuwählen.  (TBD: Button zum Melden von Fehlern geht an Sammeladresse Verkauf und Cora / uns)
                console.log(lead.name + " -- 7 -- PEPSI matching status: lead.matching", lead.match.status?.contracts);
                lead.convertToBusinessPartner = "allow-businessPartner"
                lead.convertToBusinessPartnerColor = "available-always"
                lead.matching.score = lead.match.confidence - 5;
              }
            } else {
              // Neukunden(matched auf GP mit niedriger Wahrscheinlichkeit):
              if (lead.match?.status?.idCurrentSalesPitch && lead.matchLeadId) {
                // o Rot: Google Eintrag(über GoogleId an virtuellen GPs) ist bei anderem SM oder auch in eigener Bearbeitung(aktive WVL oder Termin) => es wird die Übernahme blockiert, es wird angezeigt „in Bearbeitung: [Status / TBD Name SM]“
                console.log(lead.name + " -- 8 -- PEPSI matching status: lead.matching", lead.match.status?.contracts);
                lead.convertToBusinessPartner = "block"
                lead.convertToBusinessPartnerColor = "unavailable"
                lead.matching.score = 2 + lead.match.confidence;
              } else if (lead.match.status?.idCurrentSalesPitch) {
                // o	DunkelGrün: GP ist bei anderem SM oder auch in eigener Bearbeitung(aktive WVL oder Termin) => Er hat nur die Möglichkeit den Google Treffer zu übernehmen
                // lead.match.status.contracts = `- i.B. ${lead.match.status.salesAgent.firstName} ${lead.match.status.salesAgent.lastName} -`
                console.log(lead.name + " -- 9 -- PEPSI matching status: lead.matching", lead.match.status?.contracts);
                lead.convertToBusinessPartner = "allow-lead"
                lead.convertToBusinessPartnerColor = "check"
                lead.matching.score = lead.match.confidence - 8;
              } else {
                // o	DunkelGrün: Weder GP noch Google Eintrag ist in Bearbeitung => SM hat die Auswahl den GP oder den Google Treffer zu übernehmen
                console.log(lead.name + " -- 10 -- PEPSI matching status: lead.matching", lead.match.status?.contracts);
                lead.convertToBusinessPartner = "allow-both"
                lead.convertToBusinessPartnerColor = "available-check"
                lead.matching.score = lead.match.confidence - 8;
              }
            }
            createHighlightedTokens(lead)
          } else {
            // •	Neukunden(matched keinen GP):
            // o	DunkelGrün: Google Eintrag ist nicht in Bearbeitung => Er hat die Möglichkeit den Google Treffer zu übernehmen
            lead.convertToBusinessPartner = "allow-lead"
            lead.convertToBusinessPartnerColor = "available-always"
            lead.matching.allowSelect = true;
            lead.matching.score = -10;
          }

          const quality = buildQualityAssessment(lead);
          lead.matching.weightedScore = quality.score;
          lead.matching.qualityBucket = quality.bucket;
          // If matching indicates a central lock, force blocked styling
          if (lead.matching?.locked || lead.locked === 1) {
            lead.convertToBusinessPartner = 'block';
            lead.convertToBusinessPartnerColor = 'unavailable';
          }
          lead.matching.reasons = quality.reasons;

          lead.processed = true
          console.log(`processQuery: lead #${idx} processed, matched candidate id=${lead.match?.id} confidence=${lead.match?.confidence}`);
        } else {
          // No candidates returned -> mark as processed (no match found)
          lead.processed = true;
          lead.match = null;
          lead.convertToBusinessPartner = 'allow-lead';
          lead.convertToBusinessPartnerColor = 'available-always';
          lead.matching.score = -10;
          console.log(`processQuery: lead #${idx} processed, no candidates returned (no match)`);
        }
      }

      // After loop, log any leads that are still unprocessed for inspection
      try {
        const unprocessed = this.leadList.data.filter(l => !l.processed).map(l => ({ id: l?.meta?.googlePlaces?.id || l?.id, name: l?.name }));
        if (unprocessed.length > 0) console.warn('processQuery: unprocessed leads after loop:', unprocessed);
      } catch (e) {
        console.error('processQuery: error computing unprocessed list', e);
      }

      // Debugging summary: Anzahl der Leads und Status nach Verarbeitung
      try {
        const total = this.leadList.data.length;
        const processed = this.leadList.data.filter(l => l.processed === true).length;
        const errors = this.leadList.data.filter(l => l.error === true).length;
        console.log('processQuery: summary -> total:', total, 'processed:', processed, 'errors:', errors);
      } catch (e) {
        console.error('processQuery: error computing summary', e);
      }

      // Additional bucket counts for debugging UI mismatch
      try {
        const userStore = useUserStore();
        const isDateUnassignActiveLocal = (dateUnassign) => {
          if (!dateUnassign) return false;
          const parsed = new Date(dateUnassign);
          return !isNaN(parsed.getTime()) && parsed.getTime() > Date.now();
        };

        const total = this.leadList.data.length;
        const processed = this.leadList.data.filter(l => l.processed === true).length;
        const errorCount = this.leadList.data.filter(l => l.error === true).length;
        const noMatch = this.leadList.data.filter(l => l.processed === true && l.convertToBusinessPartner !== 'block' && !l.match).length;
        const lowMatch = this.leadList.data.filter(l => l.processed === true && l.convertToBusinessPartner === 'allow-both' && !(l.match?.status?.salesAgent?.idUser === userStore.id)).length;
        const highMatch = this.leadList.data.filter(l => l.processed === true && l.convertToBusinessPartner === 'allow-businessPartner' && !isDateUnassignActiveLocal(l.match?.status?.dateUnassign) && !(l.match?.status?.salesAgent?.idUser === userStore.id)).length;
        const noTakeover = this.leadList.data.filter(l => l.processed === true && ((l.convertToBusinessPartner === 'block') || isDateUnassignActiveLocal(l.match?.status?.dateUnassign)) && !(l.match?.status?.salesAgent?.idUser === userStore.id)).length;
        const ownMatch = this.leadList.data.filter(l => l.processed === true && (l.match?.status?.salesAgent?.idUser === userStore.id)).length;

        console.log('processQuery: buckets -> total:', total, 'processed:', processed, 'errors:', errorCount, 'noMatch:', noMatch, 'lowMatch:', lowMatch, 'highMatch:', highMatch, 'noTakeover:', noTakeover, 'ownMatch:', ownMatch);
      } catch (e) {
        console.error('processQuery: error computing buckets', e);
      }

      // Detailed per-lead status for debugging
      try {
        const perLead = this.leadList.data.map((l, i) => ({
          idx: i,
          id: l?.meta?.googlePlaces?.id || l?.id,
          name: l?.name,
          processed: !!l.processed,
          error: !!l.error,
          matchId: l.match?.id || null,
          convertToBusinessPartner: l.convertToBusinessPartner || null,
        }));
        console.log('processQuery: perLead status:', perLead);
      } catch (e) {
        console.error('processQuery: error computing perLead status', e);
      }


      // helper function to highlight similarities between google lead and matched businessPartner
      function createHighlightedTokens(lead) {
        Object.assign(lead.matching, {
          name: {
            indicator: 'ok',
            tokens: [],
          },
          address: {
            indicator: 'ok',
            tokens: [],
          },
          phoneNumbers: {
            indicator: 'ok',
            tokens: [],
          },
          urls: {
            indicator: 'ok',
            tokens: [],
          },
        });
        let matchTokens = null
        // match the tokens on the businessPartners name
        const nameTokens = lead.name.split(' ');
        matchTokens = lead.match.name.split(' ').map((token) => token.toLowerCase());
        for (const nameToken of nameTokens) {
          if (matchTokens.includes(nameToken.toLowerCase())) {
            lead.matching.name.tokens.push({ token: nameToken, match: true });
          } else {
            lead.matching.name.tokens.push({ token: nameToken, match: false });
          }
        }
        // match the tokens on the businessPartners address
        if (lead.meta.googlePlaces.address && lead.match.meta.normalization?.address) {
          const addressTokens = lead.meta.googlePlaces.address?.split(',').splice(0, 2);
          matchTokens = lead.match.meta.normalization?.address?.split(',').splice(0, 2).map((token) => token.toLowerCase());
          if (lead.match.distance === 0) {
            for (const addressToken of addressTokens) lead.matching.address.tokens.push({ token: addressToken, match: true });
          } else {
            for (const addressToken of addressTokens) {
              if (matchTokens.includes(addressToken.toLowerCase())) {
                lead.matching.address.tokens.push({ token: addressToken, match: true });
              } else {
                lead.matching.address.tokens.push({ token: addressToken, match: false });
              }
            }
          }
        } else {
          if (lead.meta.googlePlaces?.address) lead.matching.address.tokens.push({ token: lead.meta.googlePlaces.address.split(',').splice(0, 2).join(', '), match: false });
        }
        // match the phone numbers
        if (lead.meta.googlePlaces.phoneNumbers && lead.match?.meta?.normalization?.phoneNumbers && lead.meta.googlePlaces.phoneNumbers.length > 0 && Array.isArray(lead.match?.meta?.normalization?.phoneNumbers)) {
          const phoneNumberTokens = (lead.meta.googlePlaces.phoneNumbers.length > 0) ? lead.meta.googlePlaces.phoneNumbers[0].split(" ") : [];
          matchTokens = (lead.match.meta.normalization?.phoneNumbers.length > 0) ? lead.match.meta.normalization?.phoneNumbers : [];
          console.log('phoneNumberTokens:', phoneNumberTokens, 'matchTokens:', matchTokens);
          // check if any of the businessPartner phoneNubers start with the prefix of the lead number
          if (matchTokens.find((phoneNumber) => phoneNumber?.startsWith(phoneNumberTokens[0]))) {
            lead.matching.phoneNumbers.tokens.push({ token: phoneNumberTokens[0], match: true });
          } else {
            lead.matching.phoneNumbers.tokens.push({ token: phoneNumberTokens[0], match: false });
          }
          // see if the number can be found
          if (matchTokens.find((phoneNumber) => phoneNumber?.includes(phoneNumberTokens[1]))) {
            lead.matching.phoneNumbers.tokens.push({ token: phoneNumberTokens[1], match: true });
          } else {
            lead.matching.phoneNumbers.tokens.push({ token: phoneNumberTokens[1], match: false });
          }
        } else {
          if (lead.meta.googlePlaces?.phoneNumbers) lead.matching.phoneNumbers.tokens.push({ token: lead.meta.googlePlaces.phoneNumbers[0], match: false });
        }
        // match the url tokens by splitting on '.' and removing protocol and slashes
        if (
          lead.meta.googlePlaces.urls &&
          lead.meta.googlePlaces.urls.length > 0 &&
          typeof lead.meta.googlePlaces.urls[0] === 'string' &&
          lead.meta.googlePlaces.urls[0] &&
          lead.match.meta.normalization?.urls &&
          lead.match.meta.normalization.urls.length > 0 &&
          typeof lead.match.meta.normalization.urls[0] === 'string' &&
          lead.match.meta.normalization.urls[0]
        ) {
          let hostLead = null;
          let hostBusinessPartner = null;
          // Entferne Protokoll und Splitte auf '.'
          const cleanUrl = (url) => url.replace(/^https?:\/\//, '').replace(/\/$/, '');
          const urlTokens = (lead.meta.googlePlaces.urls.length > 0)
            ? lead.meta.googlePlaces.urls.flatMap((url) => cleanUrl(url).split('.').filter(t => t && t !== 'www'))
            : [];
          try {
            // Only try to parse if it looks like a valid URL
            if (/^https?:\/\//.test(lead.meta.googlePlaces.urls[0])) {
              hostLead = new URL(lead.meta.googlePlaces.urls[0]).host;
              hostLead = hostLead.replace('www.', '');
            }
          } catch (error) {
            console.error('Error parsing URL:', lead.meta.googlePlaces.urls[0], error);
          }
          try {
            if (lead.match.meta.normalization.urls?.length > 0 && lead.match.meta.normalization.urls[0]) {
              let urlBusinessPartner = lead.match.meta.normalization.urls[0];
              if (urlBusinessPartner && !/^https?:\/\//.test(urlBusinessPartner)) urlBusinessPartner = `http://${urlBusinessPartner}`;
              if (/^https?:\/\//.test(urlBusinessPartner)) {
                hostBusinessPartner = new URL(urlBusinessPartner).host;
                hostBusinessPartner = hostBusinessPartner.replace('www.', '');
              }
            }
          } catch (error) {
            console.error('Error parsing URL:', lead.match.meta.normalization.urls[0], error);
          }
          if (hostLead && hostBusinessPartner && hostLead === hostBusinessPartner) {
            for (const urlToken of urlTokens) lead.matching.urls.tokens.push({ token: urlToken, match: true });
          } else {
            matchTokens = (lead.match.meta.normalization?.urls.length > 0)
              ? lead.match.meta.normalization.urls.flatMap((url) => cleanUrl(url).split('.').filter(t => t && t !== 'www'))
              : [];
            for (const urlToken of urlTokens) {
              if (matchTokens.includes(urlToken)) {
                lead.matching.urls.tokens.push({ token: urlToken, match: true });
              } else {
                lead.matching.urls.tokens.push({ token: urlToken, match: false });
              }
            }
          }
        } else {
          if (lead.meta.googlePlaces?.urls) lead.matching.urls.tokens.push({ token: lead.meta.googlePlaces.urls[0], match: false });
        }

      }
    },
    /** Ruft das SMA Backend auf um die Google Places Suche durchzuführen
     * @async
     * @param {string} query - Der Suchstring, z.B. 'Maler Krefeld'
     * @returns {Promise<void>} Die neue liste an google places leads wird im state gespeichert und kann über den leadList getter abgerufen werden
    */
    async queryGooglePlaces(query) {
      const signature = `googlePlacesSearch:${encodeURI(query)}`;

      const userStore = useUserStore();
      userStore.currentSelection.leadGeneration.search.error = false;
      userStore.currentSelection.leadGeneration.signature = signature;

      // Nachschauen ob es zu der query bereits aus den letzten 24h Daten gibt, wenn ja diese benutzen
      let googlePlacesLeads = null;
      googlePlacesLeads = this.leadLists.find((list) => list.signature === signature);
      if (googlePlacesLeads && googlePlacesLeads?.update > (Date.now() - (24 * 60 * 60 * 1000))) {
        console.log(`queryGooglePlaces: using stored leadList: ${googlePlacesLeads} for query ${query}`);
        return googlePlacesLeads
      };

      // Es gibt keine aktuelle Liste zu der query => sofort als loading einfügen,
      // damit die UI ohne Lücke von Dummy auf aktive Suche wechselt.
      googlePlacesLeads = {
        signature: signature,
        update: Date.now(),
        isLoading: true,
        data: [],
      };
      this.leadLists.push(googlePlacesLeads);

      try {
        const response = await fetchFromBackend("businessPartners/v1/getBusinessPartnersFromGoogle",
          {
            id: userStore.id,
            query: query,
            fields: ["id", "meta"],
            options: { "match": false }
          }
        );

        if (Array.isArray(response?.businessPartners)) {
          // Alle businessPartner in der Liste bekommen ein processed Flag und ein definiertes locked-Flag (0/1)
          googlePlacesLeads.data = response.businessPartners.map((lead) => Object.assign(lead, {
            processed: false,
            locked: (typeof lead.locked !== 'undefined' && (lead.locked === 1 || lead.locked === true)) ? 1 : 0,
          }));
        } else {
          googlePlacesLeads.data = [];
          console.warn(`queryGooglePlaces: no businessPartners array in response for query ${query}`);
        }
      } finally {
        googlePlacesLeads.isLoading = false;
      }

      // HACK // Trigger reactivity manually if necessary
      this.leadLists = [...this.leadLists];

    },
    /** nutzt die Hilfsfunktion matchLeadsIsAvailable, um festzustellen, ob die Matching Funktion grundsätzlich verfügbar ist    */
    async checkMatchLeadsIsAvailable() {
      console.log('POST: checkMatchLeadsIsAvailable: Checking if matchLeads is available');
      this.matchLeadsIsAvailable = await fetchFromBackend('businessPartners/v1/matchLeadsIsAvailable', {});
    },
    /** match a google places lead against the businessPartner index to see if we alreaddy know the businessPartner
     * @async
     * @param {Object} lead - A lead object as returned by googlePlaces
     * @returns {Promise<Object>} An array of 0 or more businessPartners that match the lead with confidence and distance
    */
    async matchLeadAgainstBusinessPartnerIndex(lead) {
      const endpoint = "businessPartners/v1/matchLeads";
      const payload = {
        businessPartner: {
          id: lead.meta.googlePlaces.id,
          name: lead.name,
          address: lead.meta.googlePlaces?.address?.replaceAll(", Germany", ", Deutschland"),
          phoneNumbers: (lead.meta.googlePlaces?.phoneNumbers) ? lead.meta.googlePlaces.phoneNumbers : null,
          urls: (lead.meta.googlePlaces?.urls) ? lead.meta.googlePlaces.urls : null
        },
        fields: ["id", "meta", "name", "address", "phoneNumbers", "urls"],
        options: {
          maxCandidates: 10,
          maxDistance: 15, distance: "km",
          includeRelations: {
            deprecated: "replace", contentPartner: "replace"
          },
          minConfidence: 0.4,
        }
      };
      const response = await fetchFromBackend(endpoint, payload);
      return response;
    },
    /** Fragt den Status eines businessPartners im SMA ab, z.B. um zu schauen ob dieser einem salesAgent zugeordnet ist oder Aufträge hat
     *  die Abfrage kann über die idGoogle oder die gpNo passieren
     * @async
     * @param {Object} businessPartner - das businessPartner Objekt, zu dem der Status abgefragt werden soll. 
     * @returns {Promise<Object>} Das businessPartner Objekt ergänzt um den Status
    */
    async getBusinessPartnerStatus(businessPartner) {
      const userStore = useUserStore();

      const response = await fetchFromBackend('businessPartners/v1/getBusinessPartnerStatus',
        {
          id: userStore.id,
          businessPartner: businessPartner,
          filter: {
            idCompany: userStore.currentSelection.campaign.company.id,
            idCampaign: userStore.currentSelection.campaign.id
          }
        }
      );
      return Object.assign(businessPartner, response);
    },
    /** convert the selected google leads to businessPartners the mba database
     * @async
     * @returns {Promise<boolean>} True if the conversion was successful, false if not
    */
    async convertLeads() {
      const userStore = useUserStore();
      const endpoint = "businessPartners/v1/createFromLeads";

      const leads = []

      for (const entry of this.leadList.data.filter((lead) => lead.selection && (lead.selection.google === true || lead.selection.businessPartner === true))) {
        // depending on whether the lead is a google lead or a businessPartner lead, we need to set the idGoogle or idBusinessPartner
        const lead = {
          idGoogle: (entry.selection.google) ? entry.meta.googlePlaces.id : null,
          idBusinessPartner: (entry.selection.businessPartner) ? entry.match.id : null,
          name: entry.name,
          address: {
            name: entry.meta.googlePlaces?.address?.replaceAll(", Germany", ", Deutschland")
          },
          phoneNumbers: (entry.meta.googlePlaces?.phoneNumbers) ? entry.meta.googlePlaces.phoneNumbers : null,
          urls: (entry.meta.googlePlaces?.urls) ? entry.meta.googlePlaces.urls : null,
          match: (entry.match) ? entry.match : null,
          selection: (entry.selection) ? entry.selection : null,
          updateBusinessPartner: (entry.selection.updateBusinessPartner) ? entry.selection.updateBusinessPartner : null, // this is used to update an existing businessPartner with data from the google lead
        }
        // see if its an interesting match to return it as feedback for the machine learning model
        if (entry.match && entry.convertToBusinessPartner === 'allow-both') {
          lead.feedback = {
            businessPartner_1: {
              name: entry.name,
              address: entry.meta.googlePlaces?.address?.replaceAll(", Germany", ", Deutschland"),
              phoneNumbers: (entry.meta.googlePlaces?.phoneNumbers) ? entry.meta.googlePlaces.phoneNumbers : null,
              urls: (entry.meta.googlePlaces?.urls) ? entry.meta.googlePlaces.urls : null,
              meta: { idGoogle: entry.meta.googlePlaces.id }
            },
            businessPartner_2: {
              name: entry.match?.name,
              address: entry.match?.meta?.normalization?.address,
              phoneNumbers: (entry.match?.meta?.normalization?.phoneNumbers) ? entry.match.meta.normalization.phoneNumbers : null,
              urls: (entry.match?.meta?.normalization?.urls) ? entry.match?.meta?.normalization?.urls : null,
              meta: { gpNo: entry.match?.meta?.gpNo }
            },
            isDuplicate: (entry.selection.businessPartner) ? true : false, // if the existing businessPartner was selected, it is assumed that this is based on a match (consider it a duplicate)
          }
        }
        leads.push(lead);
      }

      const payload = {
        id: userStore.id,
        campaign: {
          id: userStore.currentSelection.campaign.id,
        },
        list: {
          id: -1,
          name: decodeURIComponent(userStore.currentSelection.leadGeneration.signature.replace("googlePlacesSearch:", "")),
        },
        leads: leads,
      }
      console.log('convertLeads: payload', payload);
      const response = await fetchFromBackend(endpoint, payload);
      return response;
    },
    /** remove the leadList (query)
     * @param {string} query - The leadList to remove (based on that query)
     * @returns {Promise<boolean>} True if the conversion was successful, false if not
    */
    async removeQuery(query) {
      console.log('removeQuery: query', query);
      this.leadLists = this.leadLists.filter((list) => list.signature !== query);
    },
  },
});